// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: shared shell for the five simple-create
// popups (Glass / Hardware / Profile / Jobwork / Ecommerce).
//
// Visual style — horizontal labels (label-left, input-right) matching the
// Glass-i reference screenshot:
//   • Horizontal labels, 100 px label column, input fills the rest.
//   • Compact rhythm — 6 px Form.Item margin, 11.5 px label, 28 px control.
//   • Slate-200 borders, slate-50 header/footer bands, white body.
//   • Single 720 px modal, body padding 12/16, footer padding 8/14.
//
// The kind-specific modal supplies:
//   • kind   — one of SIMPLE_KINDS.*
//   • render — function( { form } ) → JSX of the field rows. Each row
//              declares its own <Col span/md>; the shell already wraps
//              the rows in a 24-col Row.
//
// No business logic about specific fields lives here.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Row, Space, message } from 'antd';
import { SaveOutlined, ClearOutlined } from '@ant-design/icons';
import {
  validateSimpleCreate,
  EXTENSION_LABEL_BY_KIND
} from '../../simpleCreateHelpers';
import { saveSimpleItem } from '../../simpleCreateSave';
import { KIND_HEADERS, DEFAULT_FLAGS_BY_KIND } from '../../simpleCreateConstants';
import './SimpleCreateModal.css';

function extractFriendlyError(err) {
  const msg = err && err.response && err.response.data && err.response.data.error
    ? err.response.data.error.message
    : (err && err.message);
  return msg || 'Could not save the item. Please try again.';
}

export default function SimpleCreateModalShell({
  kind,
  open,
  onClose,
  onSaved,         // (savedItem) → void
  render,
  initialValues,
  modalTitle,      // optional override for the modal frame title
  editRecord,      // when present → edit mode (PUT /items/:id + spec upsert)
  viewOnly = false,// read-only mode (no Save, all inputs disabled)
  buildSetPayload  // optional (form) => { setType, setName, components } | { _clear:true } | null
}) {
  const [form] = Form.useForm();
  const [busy, setBusy]                 = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [extensionWarning, setExtensionWarning] = useState(null);
  const [createdItem, setCreatedItem]   = useState(null);

  const isEdit = Boolean(editRecord && editRecord.id);
  const isView = Boolean(viewOnly);
  const header = KIND_HEADERS[kind] || { title: 'Create Item', subtitle: '' };
  // The success toast wants a noun, not the verb ("Glass" rather than
  // "Create Glass Product"). Strip the leading "Create " here.
  const noun = header.title.replace(/^Create\s+/i, '');
  const frameTitle = modalTitle
    || (isView ? `View ${noun}` : isEdit ? `Edit ${noun}` : header.title);

  const mergedInitial = useMemo(() => ({
    ...(DEFAULT_FLAGS_BY_KIND[kind] || {}),
    ...(initialValues || {})
  }), [kind, initialValues]);

  // Reset on open so a previously-aborted modal doesn't leak its values
  // into the next session.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(mergedInitial);
      setValidationErrors([]);
      setExtensionWarning(null);
      setCreatedItem(null);
    }
  }, [open, form, mergedInitial]);

  const handleClear = useCallback(() => {
    form.resetFields();
    form.setFieldsValue(mergedInitial);
    setValidationErrors([]);
    setExtensionWarning(null);
    setCreatedItem(null);
  }, [form, mergedInitial]);

  const handleSave = useCallback(async () => {
    setValidationErrors([]);
    setExtensionWarning(null);
    let values;
    try {
      values = await form.validateFields();
    } catch (_info) {
      setValidationErrors(['Please fix the highlighted fields and try again.']);
      return;
    }
    const friendlyErrors = validateSimpleCreate(kind, values);
    if (friendlyErrors.length > 0) {
      setValidationErrors(friendlyErrors);
      return;
    }
    // Stage 6B.8A-3H — allow the popup to attach a set payload that
    // the save orchestration will PUT/DELETE against /:id/set after
    // the core upsert. We compute it BEFORE the request so a popup
    // validation error here can short-circuit the save.
    let setPayload = null;
    if (typeof buildSetPayload === 'function') {
      try {
        setPayload = buildSetPayload(form);
      } catch (e) {
        setValidationErrors([e.message || 'Hardware set configuration is invalid.']);
        return;
      }
    }

    setBusy(true);
    try {
      const result = await saveSimpleItem(kind, values, {
        ...(isEdit ? { editId: editRecord.id } : {}),
        ...(setPayload ? { setPayload } : {})
      });
      setCreatedItem(result.item);
      if (result.extensionOk) {
        message.success(isEdit ? `${noun} updated.` : `${noun} created.`);
        onSaved && onSaved(result.item);
      } else {
        const extLabel = EXTENSION_LABEL_BY_KIND[kind] || 'extension';
        setExtensionWarning(
          isEdit
            ? `Core fields were saved, but ${extLabel} details could not be saved. Try again or reopen the item.`
            : `Main item was saved, but ${extLabel} details could not be saved. Try again or reopen the item.`
        );
        onSaved && onSaved(result.item);
      }
    } catch (err) {
      setValidationErrors([extractFriendlyError(err)]);
    } finally {
      setBusy(false);
    }
  }, [form, kind, noun, onSaved, isEdit, editRecord, buildSetPayload]);

  const footer = isView ? (
    <Space size={8}>
      <Button type="primary" onClick={onClose}>Close</Button>
    </Space>
  ) : (
    <Space size={8}>
      {!isEdit ? (
        <Button icon={<ClearOutlined />} onClick={handleClear} disabled={busy}>
          Clear
        </Button>
      ) : null}
      <Button onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Button
        type="primary"
        icon={<SaveOutlined />}
        loading={busy}
        onClick={handleSave}
        disabled={!!extensionWarning}
      >
        {isEdit ? 'Save Changes' : 'Save Product'}
      </Button>
    </Space>
  );

  return (
    <Modal
      title={frameTitle}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={720}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-simple-create-modal"
    >
      {validationErrors.length > 0 ? (
        <Alert
          type="error" showIcon style={{ marginBottom: 8 }}
          message={validationErrors.length === 1 ? 'Please fix this:' : 'Please fix the following:'}
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
              {validationErrors.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          }
        />
      ) : null}

      {extensionWarning ? (
        <Alert
          type="warning" showIcon style={{ marginBottom: 8 }}
          message="Saved with a warning"
          description={extensionWarning}
        />
      ) : null}

      <Form
        form={form}
        layout="horizontal"
        labelCol={{ flex: '100px' }}
        wrapperCol={{ flex: 'auto' }}
        labelAlign="left"
        colon={false}
        initialValues={mergedInitial}
        autoComplete="off"
        preserve={false}
        disabled={isView}
        className="erp-simple-create-form"
      >
        <Row gutter={[12, 0]}>
          {render({ form })}
        </Row>
      </Form>
    </Modal>
  );
}
